/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { HydratedEntityType } from "@1js/pl-types";
import { Tag, Text, Tooltip, mergeClasses } from "@fluentui/react-components";
import parse from "html-react-parser";
import * as React from "react";
import type { IFilterXSSOptions } from "xss";
import { filterXSS } from "xss";
import { EntityIcon } from "../../EntityIcons";
import type { Appearance, HydratedEntity } from "../../data";
import { useStyles } from "./PromptCommand.styles";

export type EntityType =
  | "Channel"
  | "Chat"
  | "Connector"
  | "Email"
  | "Event"
  | "External"
  | "File"
  | "LoopWS"
  | "Meeting"
  | "Message"
  | "People"
  | "Person"
  | "Site"
  | "SPFolder"
  | "SPList"
  | "SPSite";

export type Entity = {
  type: EntityType;
  referenceId: string;
  accessUrl?: string;
  fileName?: string;
};

export type PromptCommandProps = {
  command: string;
  isTooltipEnabled?: boolean;
  appearance?: Appearance;
  showEntityIcon?: boolean;
  entities?: HydratedEntity[];
  className?: string;
};

// ref:https://microsoft.sharepoint.com/:w:/r/teams/msai/_layouts/15/Doc.aspx?sourcedoc=%7B7FBCBFCF-6F71-43D5-B682-BDE49E25DDF7%7D&file=M365%20Chat%20-%20Engineering%20Spec%20-%20Prompt%20Placeholder%20Schema.docx&action=default&mobileredirect=true&share=IQHPv7x_cW_VQ7aCveSeJd33AfCZbGs-46NAZ3mu9X3Zt4c&isSPOFile=1&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiIyNy8yMzA3MjgwNjkwMCIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D
const entityTypes = [
  "channel",
  "chat",
  "connector",
  "email",
  "event",
  "external",
  "file",
  "loopws",
  "meeting",
  "message",
  "people",
  "person",
  "site",
  "spfolder",
  "splist",
  "spsite",
];

const entityTagName = "entity";
const placeholderTagName = "placeholder";
const entityTypeName = "type";
const entityReferenceIdName = "referenceid";

/**
 * Options for sanitizing HTML with XSS filter.
 * Ensure to update documentation of this entire file and its tests if this is changed.
 */
const options: IFilterXSSOptions = {
  whiteList: {
    b: [],
    i: [],
    u: [],
    ul: [],
    ol: [],
    li: [],
    br: [],
    a: ["target", "href"],
    [entityTagName]: [entityTypeName, entityReferenceIdName],
    [placeholderTagName]: [],
  },
  stripIgnoreTag: false,
  allowCommentTag: true,
};

/**
 * Options for sanitizing HTML with XSS filter when everything should be encoded.
 * Ensure to update documentation of this entire file and its tests if this is changed.
 */
const optionsNoHtmlAllowed = {
  whiteList: {},
  stripIgnoreTag: false,
  allowCommentTag: true,
};

/**
 * Get the display name of the entity based on its type.
 * @param entity HydratedEntity
 * @returns The display name of the entity or undefined if not found.
 */
export const getEntityDisplayName = (entity?: HydratedEntity) => {
  if (!entity) {
    return undefined;
  }

  const entityType = entity.Type;
  switch (entityType) {
    case "People":
    case "Person" as HydratedEntityType:
      return entity.Entity.DisplayName;
    case "File":
      return entity.Entity.FileName;
    case "Event":
    case "Meeting" as HydratedEntityType:
      return entity.Entity.Subject;
  }

  return undefined;
};

/**
 * Specifies the tag type of the last encountered tag, iff the tag is
 * not allowed due to its attributes. This is used to encode closing
 * tags if the opening tag was not allowed due to attributes.
 */
let lastTagTypeNotAllowed: string | undefined = undefined;

/**
 * Handles XSS onTag sanitization for allowed tags.
 * If the allowed tag contains any forbidden attributes,
 * the opening and closing tags will be encoded, but its
 * children will be evaluated independently.
 *
 * Examples based on options and optionsNoHtmlAllowed:
 * <a data-foo="bar"><i>Hello</i></a> becomes "&lt;a data-foo=\"bar\"><i>Hello</i>&lt;/a&gt;"
 * <a><i>Hello</i></a> becomes <a><i>Hello</i></a>
 * <a><i data-foo="bar">Hello</i></a> becomes <a>&lt;i data-foo="bar"&gt;Hello&lt;/i&gt;</a>
 * <i>Hello</i> becomes <i>Hello</i>
 * @param tag The tag name.
 * @param html The HTML string.
 * @param __ Additional parameters.
 * @returns Sanitized HTML string representing the allowed tag.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleOnTag = (tag: string, html: string, options: any) => {
  let currentTagContainsNotAllowedAttribute = false;

  const optionsSanitizeAllowedTagForForbiddenAttribute = {
    ...options,
    onIgnoreTagAttr: (tag: string, name: string, _: string) => {
      if (options.whiteList?.[tag]?.includes(name) ?? false === false) {
        currentTagContainsNotAllowedAttribute = true;
        lastTagTypeNotAllowed = tag;
      } else {
        lastTagTypeNotAllowed = undefined;
      }

      // apply the xss config, i.e. do not return.
    },
  };

  const isTagClosingOfNotAllowedOpeningWithAttributes =
    tag === lastTagTypeNotAllowed && options.isClosing;
  if (isTagClosingOfNotAllowedOpeningWithAttributes) {
    lastTagTypeNotAllowed = undefined;
    return filterXSS(html, optionsNoHtmlAllowed);
  }

  const isTagClosingWithAnyArguments =
    (html.includes(" ") || html.includes("=")) && options.isClosing;
  if (isTagClosingWithAnyArguments) {
    return filterXSS(html, optionsNoHtmlAllowed);
  }

  filterXSS(html, optionsSanitizeAllowedTagForForbiddenAttribute);
  if (currentTagContainsNotAllowedAttribute) {
    return filterXSS(html, optionsNoHtmlAllowed);
  }

  // If the tag is allowed, apply the xss config, i.e. do not return.
};

/**
 * Sanitizes the prompt command by filtering out XSS and
 * replacing entity and placeholder tags with React components.
 * @param command
 * @param entityTag
 * @param placeholderTag
 * @param entities
 * @returns Sanitized HTML string with entities and placeholders replaced.
 */
export const sanitizePromptCommand = (
  command: string,
  entityTag: (
    children: JSX.Element,
    entity?: HydratedEntity,
    isHydrated?: boolean
  ) => JSX.Element,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  placeholderTag: (children: any) => JSX.Element,
  entities?: HydratedEntity[]
) => {
  const optionsFirstPass: IFilterXSSOptions = {
    ...options,
    onTag: handleOnTag,
  };
  const sanitizedHtml = filterXSS(command, optionsFirstPass);

  const sanitizedHtmlWithEntitiesAndPlaceholders = parse(sanitizedHtml, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    replace: (domNode: any) => {
      if (domNode.tagName === entityTagName) {
        const entityType: string = domNode.attribs[entityTypeName]
          .toLowerCase()
          .trim();
        if (entityTypes.includes(entityType) && domNode.children.length > 0) {
          const referenceId = domNode.attribs[entityReferenceIdName]
            ?.toLowerCase()
            .trim();
          const entity = entities?.find(
            (entity) =>
              entity.Entity.ReferenceId.trim().toLowerCase() === referenceId
          );
          return entityTag(
            getEntityDisplayName(entity) || domNode.children[0].data,
            entity,
            !!referenceId
          );
        }
      }

      if (
        domNode.tagName === placeholderTagName &&
        domNode.children.length > 0
      ) {
        return placeholderTag(domNode.children[0].data);
      }
    },
  });

  return sanitizedHtmlWithEntitiesAndPlaceholders;
};

export const PromptCommand: React.FC<PromptCommandProps> = ({
  isTooltipEnabled,
  command,
  appearance,
  showEntityIcon,
  entities,
  className,
}) => {
  const styles = useStyles(appearance)();
  const isGoBold = appearance === "web-v2" || appearance === "in-app-v2";
  const isRockSteady = appearance === "in-app-v3" || appearance === "web-v3";
  const sanitizedText = sanitizePromptCommand(
    command,
    (children: any, entity?: HydratedEntity, isHydrated?: boolean) => {
      return isGoBold || isRockSteady ? (
        <Text className={mergeClasses(styles.body, styles.entityText)}>
          {isHydrated ? "" : "/"}
          {children}
        </Text>
      ) : (
        <Tag
          appearance="filled"
          size="small"
          shape="rounded"
          className={styles.tag}
          media={
            showEntityIcon && entity ? (
              <EntityIcon entity={entity} iconSize={16} />
            ) : undefined
          }
        >
          <Text className={mergeClasses(styles.body, styles.tagText)}>
            {children}
          </Text>
        </Tag>
      );
    },
    (children: JSX.Element) => {
      return <Text className={styles.placeholder}>[{children}]</Text>;
    },
    entities
  );

  return isTooltipEnabled ? (
    <Tooltip content={sanitizedText} relationship="label">
      <div className={mergeClasses(styles.body, className)}>
        {sanitizedText}
      </div>
    </Tooltip>
  ) : (
    <div className={mergeClasses(styles.body, className)}>{sanitizedText}</div>
  );
};
